/**
 * Choosing files for a message, on the web (#1069 Story 4.1, Task 1, AC 1).
 *
 * ⚠️ DIALOG PLUMBING ONLY. Everything that decides what the composer sees is
 * `toStagedFile` in `stagedFile.ts`, which is pure and tested. Keep it
 * that way: logic added here is logic no test in `src/` can reach, because
 * nothing in this project runs against a real DOM.
 *
 * One hidden `<input type="file" multiple>`, created per call and removed
 * after. The browser's own dialog already covers photos, documents and, on a
 * device that has one, the camera, so native's three sources collapse to one
 * here and `webPickerSources` says so rather than leaving the composer to guess.
 */

import { stripDataUriPrefix } from './messageFileBytes';
import { keepReadableFiles, toStagedFile } from './stagedFile';

import type { PickResult, PickSource } from './messageFilePicker';
import type { StagedFile } from './stagedFile';
import type { InlineImageFile } from '../../components/textArea/inlineImage.types';

/** Web has one dialog. See the file header. */
export const webPickerSources: readonly PickSource[] = ['files'];

const readAsStagedFile = (file: File): Promise<StagedFile> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(new Error(`Could not read ${file.name || 'the file'}.`));
    reader.onload = () =>
      resolve(
        toStagedFile({
          name: file.name,
          mimeType: file.type,
          base64: stripDataUriPrefix(String(reader.result ?? '')),
        }),
      );
    reader.readAsDataURL(file);
  });

/**
 * A browser `File` the EDITOR handed over (a paste, a drop, the toolbar's
 * picker), read into the same `StagedFile` a strip file is (#1530). The
 * composer marks it `inline`; the bytes travel in the message body exactly as
 * a strip file's do (C3).
 */
export const stageInlineImageFile = (
  file: InlineImageFile,
): Promise<StagedFile> => {
  if (!(file instanceof File)) {
    return Promise.reject(new Error('Could not read the image.'));
  }
  return readAsStagedFile(file);
};

export const pickMessageFiles = (_source: PickSource): Promise<PickResult> =>
  new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.style.display = 'none';

    input.addEventListener('change', () => {
      const chosen = Array.from(input.files ?? []);
      input.remove();
      if (chosen.length === 0) {
        // Cancelled. Not an error, and the already-staged files survive
        // because the composer never cleared them (AC 4).
        resolve({ files: [], error: null });
        return;
      }
      // ⚠️ ALL OF THEM, IN ORDER (AC 4, "never silently truncate a
      // multi-select"). The count and size ceilings belong to the composer,
      // which refuses WITH A REASON. Dropping files here would refuse them
      // silently, which is the one thing that AC forbids.
      //
      // ⚠️ AND A SINGLE UNREADABLE FILE MUST NOT TAKE THE OTHERS WITH IT (code
      // review 2026-09-13). This was `Promise.all(...).catch(() => resolve([]))`:
      // one FileReader error — a file on a network share, a removable drive
      // pulled out — rejected the whole batch, resolved with nothing, and the
      // panel read the empty array as a user cancel and said NOTHING. Three
      // chosen photos vanished with no message, which is AC 4's silent
      // truncation at 100%. `allSettled` keeps what could be read and rejects
      // only if NOTHING could, so the panel's "could not be read" line is
      // reachable from web at last.
      // The RULE lives in `keepReadableFiles` (no DOM needed, and tested);
      // this is the wiring that cannot be. ⚠️ BOTH HALVES TRAVEL: rejecting on
      // a partial loss would throw away the files that DID read.
      Promise.allSettled(chosen.map(readAsStagedFile)).then(results => {
        const { staged, error } = keepReadableFiles(
          results,
          chosen.map(f => f.name),
        );
        resolve({ files: staged, error });
      });
    });

    // ⚠️ A DISMISSED DIALOG FIRES `cancel`, NOT `change` (code review
    // 2026-09-13). With `change` as the only listener, pressing Escape left the
    // promise permanently unsettled and the hidden `<input>` in the document —
    // one more on every cancel. Survivable only because nothing awaits this
    // behind a busy flag, which is not a property to rely on.
    //
    // ⚠️ ON BROWSERS THAT SEND IT, WHICH IS NOT ALL OF THEM (code review run 3,
    // 2026-09-13). The HTML `cancel` event on `<input type=file>` is absent in
    // Safari before 16.4 and in older Firefox, where the pre-existing hang and
    // the leaked element both remain. This comment previously declared the bug
    // fixed unconditionally. Not worked around with a focus/visibility heuristic
    // because those fire on other things too and would resolve a pick the user
    // had not finished making; the honest state is "fixed on current browsers".
    input.addEventListener('cancel', () => {
      input.remove();
      resolve({ files: [], error: null });
    });

    // ⚠️ APPENDED, NOT LEFT FLOATING: Safari will not open the dialog for an
    // input that is not in the document.
    document.body.appendChild(input);
    input.click();
  });
